import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { BarChart3, TrendingUp } from 'lucide-react'
import { dashboardService, AssetFlowData } from '@/services/dashboardService'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

type PeriodType = 'today' | 'month' | 'custom'

const AssetFlowChart = () => {
  const { t } = useTranslation(['dashboard', 'common'])
  // 이번 주 월요일과 오늘 날짜 계산
  const getThisWeekDates = () => {
    const today = new Date()
    const day = today.getDay()
    const diff = today.getDate() - day + (day === 0 ? -6 : 1)
    const monday = new Date(today.setDate(diff))
    const todayDate = new Date()

    return {
      start: monday.toISOString().split('T')[0],
      end: todayDate.toISOString().split('T')[0]
    }
  }

  const thisWeek = getThisWeekDates()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<AssetFlowData | null>(null)
  const [period, setPeriod] = useState<PeriodType>('month')
  const [chartType, setChartType] = useState<'line' | 'bar'>('bar')
  const [startDate, setStartDate] = useState(thisWeek.start)
  const [endDate, setEndDate] = useState(thisWeek.end)

  const loadData = async () => {
    try {
      setLoading(true)
      const result = await dashboardService.getAssetFlow(period, startDate, endDate)
      setData(result)
    } catch (error) {
      console.error('차트 데이터 로딩 실패:', error)
      toast.error(t('dashboard:assetFlow.chartLoadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriod(newPeriod)

    if (newPeriod === 'custom') {
      const week = getThisWeekDates()
      setStartDate(week.start)
      setEndDate(week.end)
    }
  }

  const handleCustomSearch = () => {
    if (!startDate || !endDate) {
      toast.error(t('dashboard:assetFlow.selectDatesError'))
      return
    }
    loadData()
  }

  useEffect(() => {
    loadData()
  }, [period])

  if (loading) {
    return (
      <div className="glass-dark p-6">
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </div>
    )
  }

  if (!data || !data.chart_data || data.chart_data.length === 0) {
    return (
      <div className="glass-dark p-6">
        <div className="flex items-center justify-center h-64 text-dark-400">{t('dashboard:assetFlow.noChartData')}</div>
      </div>
    )
  }

  // 날짜 포맷팅
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  const chartData = data.chart_data.map(item => ({
    ...item,
    date: formatDate(item.date)
  }))

  return (
    <div className="glass-dark p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6" />
            <span>{t('dashboard:assetFlow.chartTitle')}</span>
          </h2>
          <p className="text-sm text-dark-400 mt-1">{t('dashboard:assetFlow.dailyStatus')}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Filter */}
          <button
            onClick={() => handlePeriodChange('today')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              period === 'today' ? 'bg-primary-500 text-white' : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.today')}</button>
          <button
            onClick={() => handlePeriodChange('month')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              period === 'month' ? 'bg-primary-500 text-white' : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.thisMonth')}</button>
          <button
            onClick={() => handlePeriodChange('custom')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              period === 'custom' ? 'bg-primary-500 text-white' : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.customPeriod')}</button>

          <div className="w-px h-6 bg-dark-600"></div>

          {/* Chart Type Toggle */}
          <button
            onClick={() => setChartType('bar')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              chartType === 'bar' ? 'bg-primary-500 text-white' : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.barChart')}</button>
          <button
            onClick={() => setChartType('line')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              chartType === 'line' ? 'bg-primary-500 text-white' : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.lineChart')}</button>
        </div>
      </div>

      {/* Custom Date Range */}
      {period === 'custom' && (
        <div className="mb-6 p-4 glass-darker rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label className="text-sm text-dark-300 mb-1 block">{t('dashboard:assetFlow.startDate')}</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-glass w-full"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm text-dark-300 mb-1 block">{t('dashboard:assetFlow.endDate')}</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-glass w-full"
              />
            </div>
            <button
              onClick={handleCustomSearch}
              className="btn-primary whitespace-nowrap"
            >{t('dashboard:assetFlow.search')}</button>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="date"
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.95)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                formatter={(value: number) => `$${value.toFixed(2)}`}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Bar
                dataKey="deposits"
                fill="#10B981"
                name={t('assetFlow.deposit')}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="withdrawals"
                fill="#EF4444"
                name={t('assetFlow.withdrawal')}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="bonus"
                fill="#F59E0B"
                name={t('assetFlow.bonus')}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="bulk_grant_money"
                fill="#3B82F6"
                name={t('assetFlow.bulkGrantUSDT')}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="date"
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.95)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                formatter={(value: number) => `$${value.toFixed(2)}`}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Line
                type="monotone"
                dataKey="deposits"
                stroke="#10B981"
                strokeWidth={2}
                name={t('assetFlow.deposit')}
                dot={{ fill: '#10B981', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="withdrawals"
                stroke="#EF4444"
                strokeWidth={2}
                name={t('assetFlow.withdrawal')}
                dot={{ fill: '#EF4444', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="bonus"
                stroke="#F59E0B"
                strokeWidth={2}
                name={t('assetFlow.bonus')}
                dot={{ fill: '#F59E0B', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="bulk_grant_money"
                stroke="#8B5CF6"
                strokeWidth={2}
                name={t('assetFlow.bulkGrantUSDT')}
                dot={{ fill: '#8B5CF6', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="net_flow"
                stroke="#3B82F6"
                strokeWidth={2}
                strokeDasharray="5 5"
                name={t('assetFlow.netChange')}
                dot={{ fill: '#3B82F6', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default AssetFlowChart
